import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import { alpha, useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { partResultLabel } from '../../../../generic/utilities/partResultLabel'
import QrCodeGeneratorDialog, { buildAvailableFields } from '../../../../generic/qr/QrCodeGeneratorDialog'
import { buildQrPayload } from '../../../../generic/qr/qrPayload'
import { QRCodeSVG } from 'qrcode.react'
import { raisedColor } from '../detailSurfaces'
import { groupMountedComponents } from '../mountedComponentGroups'
import { PART_TRACK_STEP_STATE_LEGACY_KEYS, partTrackStepReached, partTrackStepIconKey } from '../partTrackStepState'
import { SystemValueChip } from '../../../../generic/systemValues/SystemValueChip'
import { daysInProgress } from '../daysInProgress'
import CollapsedFieldsLine from './CollapsedFieldsLine'
import { CONTROL_RADIUS } from '../detailControls'
import { operationWorkersLabel } from '../operationWorkerLabels'
import { EMPTY_DASH } from '../detailShared'

// PartTrackDto's `part.lastResult`/step `result` use 'OK'|'FAIL'|null (not 'OK'|'NOK'|'NONE' like
// the grid's `result`) — this adapts the value so the SAME shared `partResultLabel` (used tal
// cual, never touched) still owns the OK/NOK i18n keys; it is a value adapter, not new logic.
const trackResultLabel = (result, t_) => partResultLabel(result === 'FAIL' ? 'NOK' : result, t_)

// S398-S400 (ADDENDUM 40 / E46) — los campos que la ficha de una PIEZA ofrece al generar su QR.
//
// ⚠️ ESTO OFRECÍA UN SOLO CAMPO (`code`), y por eso el diálogo "parecía otra ventana": es EL MISMO
// componente que abre la cabecera con catorce, solo que sin nada que componer. No era un diseño
// distinto — era la misma ventana vacía.
//
// ⚠️⚠️ LOS ANIDADOS VAN POR SU PATH (`partReference.code`), NO COMO OBJETO, y el modo de fallo no es
// el que parece: pasar `partReference` a secas NO imprime "[object Object]" — EL CAMPO DESAPARECE
// EN SILENCIO. `buildAvailableFields` filtra lo que resuelve a objeto (guarda de H2-S14, puesta
// precisamente para que ningún campo ofrecido pueda dar "[object Object]"). Comprobado ejecutándolo:
//     sin aplanar -> ["code"]
//     con path    -> ["code","partReference.code","fabricationOrder.code","batch"]
// O sea que el error no se ve como un valor feo sino como CAMPOS QUE FALTAN, que es exactamente el
// síntoma que el PO reportó y el más difícil de atribuir.
//
// 📌 Y el path elige además la CLAVE del QR, que sale del primer segmento: `fabricationOrder.code`
// da `FABRICATIONORDER=`, la misma que emite la cabecera y el listado tras el addendum 34. Usar un
// alias plano habría funcionado en pantalla y habría cambiado la clave impresa.
//
// ⚠️ `lastUpdatedAt` LLEVA SU `type`. Sin él, `formatQrValue` no normaliza y la fecha viaja CRUDA al
// código (`2026-03-15T10:00:00Z`) en vez de `2026-03-15T10:00`. Es el mismo descuido que estuvo a
// punto de colarse con `plannedEndDate` en el addendum 34.
//
// 📌 `batch` viaja casi siempre vacío y SE OFRECE IGUAL (RN8): un campo disponible con valor
// ausente es un campo que el usuario decide no poner, no un campo que debamos esconder.
//
// 📌 Y NO se unifica con el QR de la cabecera: codifican cosas distintas —la ORDEN, y ESTA PIEZA,
// que es lo que el operario escanea en taller—, así que fundirlos perdería la etiqueta de pieza.
// Que se parezcan es el objetivo; que sean uno solo, no.
// Historia 226 (S144, R7b): the titles are i18n keys, translated where the dialog is opened — the offered title is
// what gets printed on the label.
export const PART_QR_COLUMNS = [
  { field: 'code', name: 'Code' },
  { field: 'partReference.code', name: 'Part Reference' },
  { field: 'fabricationOrder.code', name: 'Fabrication Order' },
  { field: 'batch', name: 'Batch' },
  { field: 'lastResult', name: 'Result' },
  { field: 'lastUpdatedAt', name: 'Last updated', type: 'dateTimePicker' }
]

const TrackLabel = ({ children }) => (
  <Typography variant='caption' sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' }}>
    {children}
  </Typography>
)

// The "Selected part" card: the part's code, its last result and the QR that opens the generator.
export const SelectedPartCard = ({ selectedPartId, track }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const [showQr, setShowQr] = useState(false)
  const trackPart = track.data.part || {}

  return (
    <Box
      data-testid='parts-selected-card'
      sx={{
        // ⚠️⚠️ S460 — ESTO ERA `flex: 1`, que le daba el MISMO tercio que a Instances aunque
        // su contenido (un código, una píldora y un QR de 84 px) cupiera en mucho menos, y
        // dejaba hueco vacío a los dos lados. `flexGrow: 0` + un tope de ancho la compactan
        // a lo que necesita; el relleno también baja (`p: 1.25`, antes `1.5`).
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 'auto',
        maxWidth: 260,
        minWidth: 0,
        borderRadius: 2,
        border: `1px solid ${selectedPartId ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.text.primary, 0.08)}`,
        backgroundColor: raisedColor(theme),
        p: 1.25,
        textAlign: 'center'
      }}
    >
      <Typography variant='caption' sx={{ fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'text.secondary' }}>
        {t_('Selected part')}
      </Typography>
      {!selectedPartId && (
        <Typography variant='body2' sx={{ color: 'text.secondary', mt: 2 }}>{t_('Select a part to see its details')}</Typography>
      )}
      {!!selectedPartId && track.status === 'ready' && (
        <Box sx={{ mt: 1 }}>
          {/* Pasada 24: código, estado y QR en FILA en vez de apilados — la tarjeta pasa de
          ~190px de alto a ~110 y el recorrido sube esa misma distancia. */}
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='center'>
            <Box sx={{ textAlign: 'left' }}>
              <Typography data-testid='part-track-code' variant='h6' sx={{ fontWeight: 800 }}>{trackPart.code}</Typography>
              {/* ⚠ Fix (pasada 21, defecto 3): with no published result yet, `partResultLabel`
              returns '' on purpose (RN8.1/S33) — mounting the chip anyway painted an empty,
              textless pill. No label, no chip. */}
              {!!trackResultLabel(trackPart.lastResult, t_) && (
                <Chip
                  label={trackResultLabel(trackPart.lastResult, t_)}
                  data-testid='part-track-last-result'
                  size='small'
                  sx={{ fontWeight: 700, mt: 0.5 }}
                />
              )}
            </Box>

            {/* ⚠️⚠️ EL QR SE DIBUJA CON `buildQrPayload`, LA MISMA FUNCIÓN QUE USA EL
            GENERADOR, y no con una cadena armada aquí. Dos códigos en la misma pantalla
            construidos por dos caminos son dos definiciones del mismo dato, y la
            segunda es la que acaba divergiendo — es el defecto que S415 vino a quitar.
            Lo que emite esta llamada es la IDENTIDAD: `MIC-QR-PART-171`. El del
            generador añade detrás los campos que el usuario marque
            (`MIC-QR-PART-171$CODE:HUN-00024`), así que **el prefijo de identidad es
            idéntico**: quien escanee uno u otro llega a la misma pieza. Lo que cambia
            es cuánta información lleva impresa al lado, no a qué apunta.
            📌 Y por eso el botón se queda: éste es «escanea esto ya» y aquél abre el
            generador CON selección de campos. Son dos cosas distintas.
            ⚠️ Esto matiza §5.4 —«el QR grande desaparece»— y es decisión del PO: lo que
            aquel addendum quitó fue el BLOQUE de huella digital con un QR ocupando
            media pantalla, y eso sigue quitado. Esto cabe en el hueco que ya sobraba en
            una tarjeta que enseñaba dos líneas. */}
            {/* ⚠️⚠️ EL QR ES EL DISPARADOR, y por eso es un `<button>` DE VERDAD y no un
            `onClick` sobre el dibujo. Un código clicable que el tabulador se salta deja
            la acción solo para quien usa ratón — y ahí sí se habría perdido algo que el
            botón retirado sí daba.
            ⚠️ VA MUDO, por petición del PO: ni pie ni tooltip — «si se explica bien el
            producto no hace falta poner por todas partes lo que se puede hacer». La
            tarjeta queda con el código, la píldora y el QR.
            📌 LO QUE SÍ SE QUEDA, Y NO CONTRADICE ESA PETICIÓN, ES EL `aria-label`:
            **no pinta nada en pantalla**. Lo que el PO no quiere son carteles, y esto
            no pone ninguno — es lo único que permite que alguien con teclado o lector
            encuentre la acción. Quitarlo no limpiaría nada: la haría inalcanzable para
            quien no use ratón. Reutiliza el literal del botón retirado, que describe
            exactamente lo que hace y ya estaba traducido en los ocho idiomas.
            📌 Pulsar NO cambia a dónde apunta: el dibujado lleva la identidad
            (`MIC-QR-PART-171`) y el generador le añade detrás los campos que se
            marquen, así que el prefijo es el mismo y los dos llevan a la misma pieza.
            Lo que cambia es cuánta información va impresa al lado. */}
            <Box
              component='button'
              type='button'
              data-testid='part-track-qr'
              aria-label={t_('Open on the shop floor')}
              onClick={() => setShowQr(true)}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
                border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                borderRadius: CONTROL_RADIUS,
                background: 'transparent',
                cursor: 'pointer',
                '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 }
              })}
            >
              <QRCodeSVG value={buildQrPayload({ entityType: 'Part', id: trackPart.id })} size={84} level='Q' />
            </Box>
          </Stack>

          {showQr && (
            <QrCodeGeneratorDialog
              open={showQr}
              onClose={() => setShowQr(false)}
              entityType='Part'
              entityLabel={t_('Part')}
              instance={{ ...trackPart, id: trackPart.id }}
              subtitle={trackPart.code}
              availableFields={buildAvailableFields(PART_QR_COLUMNS.map(c => ({ ...c, name: t_(c.name) })), { ...trackPart, id: trackPart.id })}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

// The selected part's sheet and operation track, full width under the cards.
const PartTrackPanel = ({ track, mountedComponentsExpanded, setMountedComponentsExpanded, expandedGroups, onToggleGroup, onClose }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const { formatDateTime } = useDateFormatter()
  const trackPart = track.data.part || {}
  const trackSteps = track.data.steps || []
  const mountedComponents = trackPart.mountedComponents || []

  // §2.3 — el resumen de lo plegado. ⚠️ ENUMERA CON SU VALOR, no dice "hay campos vacíos": es lo
  // que hace que plegar no sea ocultar. Cada entrada sale solo cuando su dato NO exige acción —
  // un lote asignado o un resultado presente se leen arriba, no aquí.
  const sinResultado = trackSteps.filter((step) => !step.result).length
  const resumenPlegado = [
    trackPart.batch ? '' : t_('unassigned batch'),
    sinResultado > 0 ? t_('no result in {{count}} operations', { count: sinResultado }) : '',
    // La huella digital deja de ser un bloque y se lee aquí, entera y en una línea.
    [trackPart.fabricationOrder?.code, trackPart.partReference?.code].every(Boolean)
      ? t_('fingerprint: order {{order}}, reference {{reference}}', { order: trackPart.fabricationOrder.code, reference: trackPart.partReference.code })
      : ''
  ].filter((entrada) => entrada !== '')
  // ⚠ Nota de diagnóstico de Sun: el método que los resuelve está envuelto en un `catch (Exception)`
  // que loguea un aviso y devuelve lista vacía. Si esta lista sale vacía NO significa que la pieza no
  // tenga componentes: hay que buscar en el log `Cannot resolve mounted components of part`.
  const mountedGroups = groupMountedComponents(mountedComponents)

  return (
    <Box data-testid='part-track' sx={{ mt: 1.5 }}>
      {/* Revisión del PO (pasada 24): la huella digital es una tarjeta COMPACTA de pocos
      campos —cinco columnas en una fila, no una rejilla de tres que ocupa dos— para que
      el recorrido, que es lo que manda, empiece cuanto antes. */}
      <Box
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          backgroundColor: raisedColor(theme),
          p: 1.5
        }}
      >
        {/* ⚠️⚠️ ESTO ERA UN BLOQUE «HUELLA DIGITAL» CON CINCO CELDAS IGUALES, y §5.4 lo
        disuelve: la OF y la referencia ya están en la cabecera de la ventana, y el
        lote baja a la línea plegada del pie. Lo que se queda arriba es lo que se
        consulta al mirar una pieza — cuántos componentes lleva, de qué referencia es
        y cuándo se tocó por última vez.
        ⚠️ Los tres que bajan NO desaparecen: se leen enteros en la línea de abajo, con
        su valor. Plegar no es ocultar (§2.3). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 1.5
          }}
        >
          <Box>
            <TrackLabel>{t_('Reference')}</TrackLabel>
            <Typography data-testid='part-track-reference' variant='body2'>{trackPart.partReference?.code || EMPTY_DASH}</Typography>
          </Box>
          <Box>
            <TrackLabel>{t_('Last updated')}</TrackLabel>
            <Typography data-testid='part-track-last-updated' variant='body2'>{trackPart.lastUpdatedAt ? formatDateTime(trackPart.lastUpdatedAt) : EMPTY_DASH}</Typography>
          </Box>
          {/* ⚠️⚠️ EL LOTE SE PLIEGA SOLO SI ESTÁ VACÍO, y esta condición se me había caído.
          §2.3 pliega "todo campo que valga 0, esté vacío o sea sin dato" — no los que
          tienen valor. Mi primera versión lo bajaba siempre, y entonces un lote
          asignado solo se leía abriendo el plegable: eso ya no es plegar, es ocultar,
          que es justo lo que §2.3 dice que no hace. Lo destapó un test de S217. */}
          {!!trackPart.batch && (
            <Box>
              <TrackLabel>{t_('Batch')}</TrackLabel>
              <Typography data-testid='part-track-batch' variant='body2'>{trackPart.batch}</Typography>
            </Box>
          )}
          <Box data-testid='part-track-mounted-components' sx={{ gridColumn: { xs: 'auto', sm: 'span 2', lg: 'span 1' } }}>
            <Stack direction='row' spacing={0.75} alignItems='center' sx={{ mb: 0.5 }}>
              <TrackLabel>{t_('Mounted components')}</TrackLabel>
              {mountedComponents.length > 0 && (
                <Chip
                  size='small'
                  label={mountedComponents.length}
                  sx={{ height: 18, fontWeight: 800, backgroundColor: alpha(theme.palette.text.primary, 0.08) }}
                />
              )}
            </Stack>
            {/* Revisión del PO (pasada 24): plegados POR DEFECTO tras su recuento. Antes se
            pintaban los 633 códigos —recortados a 88px de alto, pero los 633 en el DOM—
            y eran lo primero y más grande del bloque, cuando lo que manda en el mockup
            es la tabla del recorrido. Desplegados, viven en una caja con scroll propio
            que nunca puede volver a empujar el recorrido fuera de pantalla. */}
            {mountedComponents.length === 0 && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>{t_('None')}</Typography>
            )}
            {mountedComponents.length > 0 && !mountedComponentsExpanded && (
              <Typography data-testid='part-track-mounted-components-collapsed' variant='body2' sx={{ color: 'text.secondary' }}>
                {t_('{{n}} components mounted on this part', { n: mountedComponents.length })}
              </Typography>
            )}
            {mountedComponents.length > 0 && mountedComponentsExpanded && (
            /* S359 (ADDENDUM 22 / E28): agrupados por NOMBRE con su número de unidades,
             y cada línea se abre para ver los códigos de esas unidades.
             ⚠ El desplegable es lo que salva la trazabilidad, y por eso se eligió la
             opción cara: agrupar sin más perdería DE QUÉ UNIDAD CONCRETA salió cada
             componente, y rastrear una unidad es medio motivo de que esta pantalla
             exista. Hoy no se perdería nada —0 de 277 traen lote— pero la decisión no se
             toma sobre el volcado de hoy.
             Antes se pintaban los códigos sueltos: 277 correlativos sin significado. */
              <Box
                data-testid='part-track-mounted-components-groups'
                sx={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                  p: 0.5
                }}
              >
                {mountedGroups.map((group) => {
                  const open = expandedGroups.includes(group.key)
                  return (
                    <Box key={group.key} data-testid={`mounted-group-${group.key}`}>
                      <Button
                        data-testid={`mounted-group-toggle-${group.key}`}
                        onClick={() => onToggleGroup(group.key)}
                        size='small'
                        fullWidth
                        sx={{ justifyContent: 'space-between', textTransform: 'none', px: 1, py: 0.25 }}
                      >
                        <Typography component='span' variant='body2' sx={{ fontWeight: 700, textAlign: 'left' }}>
                          {group.label}
                        </Typography>
                        <Chip
                          size='small'
                          data-testid={`mounted-group-count-${group.key}`}
                          label={`×${group.count}`}
                          sx={{ height: 18, fontWeight: 800, backgroundColor: alpha(theme.palette.text.primary, 0.08) }}
                        />
                      </Button>
                      {open && (
                        <Box
                          data-testid={`mounted-group-units-${group.key}`}
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1, pb: 1 }}
                        >
                          {group.units.map((unit, index) => (
                            <Chip
                              key={`${unit.code || group.key}-${index}`}
                              size='small'
                              label={unit.code || group.label}
                              title={unit.batch || ''}
                              sx={{ fontWeight: 700 }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Box>
            )}
            {mountedComponents.length > 0 && (
              <Button
                data-testid='part-track-mounted-components-toggle'
                onClick={() => setMountedComponentsExpanded((prev) => !prev)}
                size='small'
                sx={{ mt: 0.5, textTransform: 'none', minWidth: 0 }}
              >
                {mountedComponentsExpanded ? t_('Show less') : t_('Show all')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {trackSteps.length === 0 && (
        <Typography data-testid='part-track-empty' variant='body2' sx={{ color: 'text.secondary', mt: 2 }}>
          {t_('No operation has processed this part yet')}
        </Typography>
      )}

      {trackSteps.length > 0 && (
        <TableContainer data-testid='part-track-steps' sx={{ mt: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` }}>
          <Table size='small' aria-label={t_('Part track')}>
            <TableHead>
              <TableRow sx={{ backgroundColor: raisedColor(theme) }}>
                {/* §5.4 — el ESTADO sale a su propia columna (venía debajo del nombre) y
                «Momento» pasa a llamarse INICIO, que es lo que contiene.
                ⚠️⚠️ LO QUE §5.4 SÍ PEDÍA Y NO SE HACE: fundir operario y máquina en una
                columna «Operario · Máquina». Lo construí así y se deshizo (addendum
                50), porque esa pareja es la más traicionera de esta tabla:
                LA MÁQUINA SÍ ES DE ESTA PIEZA Y LOS OPERARIOS NO. Ninguna tabla une
                pieza con operario —`part_workflow_history` tiene pieza y no operario,
                `device_worker_fabrication_order` tiene operario y no pieza—, así que
                estos son los de la OPERACIÓN. Juntas, la veracidad de una le presta
                credibilidad a la otra, y el lector concluye que esa persona tocó esta
                pieza. Separadas, cada rótulo responde por sí mismo. */}
                <TableCell><TrackLabel>{t_('Operation')}</TrackLabel></TableCell>
                <TableCell><TrackLabel>{t_('Status')}</TrackLabel></TableCell>
                <TableCell><TrackLabel>{t_('Start')}</TrackLabel></TableCell>
                <TableCell><TrackLabel>{t_('Operation workers')}</TrackLabel></TableCell>
                <TableCell><TrackLabel>{t_('Device')}</TrackLabel></TableCell>
                <TableCell><TrackLabel>{t_('Result')}</TrackLabel></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trackSteps.map((step) => {
                const enCurso = step.state === 'IN_PROGRESS'
                const alcanzado = partTrackStepReached(step.state)
                // ⚠️ LA GUARDA DE «SOLO EN CURSO» VIVE AQUÍ Y EN UN SOLO SITIO. La tenía además en el
                // render (`enCurso && dias !== null && …`) y era redundante: con dos guardas
                // independientes, quitar cualquiera de las dos no cambia nada, así que NINGÚN sabotaje
                // mordía y el test no podía decirme cuál era la que decide. Una guarda que no se puede
                // romper tampoco se puede comprobar.
                const dias = enCurso ? daysInProgress(step.startedAt) : null
                return (
                  <TableRow
                    key={step.jobReferenceId}
                    data-testid={`part-step-${step.jobReferenceId}`}
                    hover
                    sx={(t) => ({
                      // S442 (D13) — aquí iba una barra de color de 4 px para la operación EN
                      // CURSO, y se quitó: la píldora de estado ya dice «En curso» al lado.
                      // Se queda el fondo tenue, que no es una barra.
                      ...(enCurso ? { backgroundColor: alpha(t.palette.info.main, 0.05) } : {})
                    })}
                  >
                    <TableCell>
                      <Typography component='span' data-testid='step-name' variant='body2' sx={{ fontWeight: 700, color: alcanzado ? 'text.primary' : 'text.secondary' }}>{step.name}</Typography>
                    </TableCell>
                    <TableCell>
                      {/* Story 204 (RN1.5): the label comes from the state's own code, except for
                      the P1-bis states, which keep their legacy key (PART_TRACK_STEP_STATE_LEGACY_KEYS).
                      A state without a key is painted as is: ugly, but it informs (S369). */}
                      {/* PO, 23/09: the SAME look as the Workflow and Progress pill —icon, colour,
                      size, typography—. The shared pill is now SystemValueChip with the `chip`
                      look; the pill is shared, not the vocabulary. */}
                      <SystemValueChip
                        look='chip'
                        domain='PartTrackStepState'
                        value={step.state}
                        labelKeys={PART_TRACK_STEP_STATE_LEGACY_KEYS}
                        iconKey={partTrackStepIconKey(step.state)}
                        iconTestIdPrefix='step-state-icon'
                        testId='step-state'
                      />
                    </TableCell>
                    <TableCell>
                      <Typography component='span' data-testid='step-started-at' variant='caption' sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {alcanzado ? formatDateTime(step.startedAt) : EMPTY_DASH}
                      </Typography>
                      {/* ⚠️ «N días en curso» ES UN DERIVADO DEL RELOJ, el único de esta
                      pantalla que no viene de Sun. Sale SOLO en la operación en curso
                      —en una terminada el número seguiría creciendo y mentiría— y
                      solo a partir del primer día entero: «0 días en curso» en ámbar
                      alarmaría por una operación que arrancó esta mañana. */}
                      {dias !== null && dias > 0 && (
                        <Typography component='span' data-testid='step-days-in-progress' variant='caption' sx={{ display: 'block', color: 'warning.main', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {t_('{{count}} days in progress', { count: dias })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* S392/S393: el literal de ausencia era FIJO — se pintaba en los tres
                      pasos dijera lo que dijera el servidor, porque Sun no mandaba
                      nada. Ahora manda `operationWorkers`, y el literal sale SOLO con
                      la lista vacía. Que siga apareciendo cuando toca no es que no haya
                      cambiado nada: es que la ausencia se sigue declarando (RN8). */}
                      {/* ⚠️ LA LISTA ENTERA, NUNCA UNO. `operationWorkers` es una LISTA y el
                      54% de los momentos tienen varios a la vez: pintar un nombre
                      exigiría ELEGIR, que es otra invención encima de la de atribuir
                      la pieza. `operationWorkersLabel` los escribe todos.
                      📌 «Sin operario asignado» en una pendiente SÍ es correcto y no
                      entra en el problema de arriba: una operación que no ha empezado
                      no tiene operarios, y eso no lo discute nadie. */}
                      <Typography component='span' data-testid='step-worker' variant='caption' sx={{ display: 'block', color: (step.operationWorkers || []).length === 0 ? 'text.disabled' : 'text.secondary' }}>
                        {operationWorkersLabel(step.operationWorkers) || t_('No operation worker assigned')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component='span' data-testid='step-machine' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                        {alcanzado && step.device?.name ? step.device.name : EMPTY_DASH}
                      </Typography>
                      {alcanzado && !!step.batch && (
                        <Typography component='span' data-testid='step-batch' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>{step.batch}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Sin resultado NO se deja la celda en blanco: el guion dice que
                      no lo hay, y una celda vacía se lee como un fallo de pintado. */}
                      <Typography component='span' data-testid='step-result' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                        {alcanzado && step.result ? trackResultLabel(step.result, t_) : EMPTY_DASH}
                      </Typography>
                      {alcanzado && step.result === 'FAIL' && (
                        <>
                          <Typography component='span' data-testid='step-result-description' variant='caption' sx={{ display: 'block', color: 'error.main' }}>{step.resultDescription}</Typography>
                          {!!step.incidence?.code && (
                            <Typography component='span' data-testid='step-incidence' variant='caption' sx={{ display: 'block', color: 'error.main' }}>{step.incidence.code}</Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* S393 — LA NOTA VA FIJA, esté la lista llena o vacía, y no es decoración: es lo
      único que impide que la columna se lea como "quién hizo ESTA pieza". Ese dato no
      existe en el modelo. Sun lo dice en el nombre del campo (`operationWorkers`,
      R-HANDOFF-2), pero el nombre del campo no se ve en pantalla — el usuario lee un
      rótulo y una lista de personas dentro de la ficha de UNA pieza, y sin la nota la
      lectura natural es la equivocada. Hay un test que falla si se acorta o se quita. */}
      {trackSteps.length > 0 && (
        <Typography data-testid='part-track-workers-note' variant='caption' sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
          {t_('It is the worker of the OPERATION in this order, not of this particular part')}
        </Typography>
      )}

      <CollapsedFieldsLine
        testId='part-track-collapsed'
        openLabel={t_('See full sheet')}
        summary={resumenPlegado}
      >
        {/* Lo plegado, entero y con sus rótulos. El resumen de arriba ya lo dice en una
        línea; esto es para quien necesita leerlo como campos. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          <Box>
            <TrackLabel>{t_('Fabrication order')}</TrackLabel>
            <Typography data-testid='part-track-fo' variant='body2'>{trackPart.fabricationOrder?.code || EMPTY_DASH}</Typography>
          </Box>
          {/* Sin lote, aquí también se dice — arriba no hay celda porque no hay valor. */}
          {!trackPart.batch && (
            <Box>
              <TrackLabel>{t_('Batch')}</TrackLabel>
              <Typography variant='body2'>{EMPTY_DASH}</Typography>
            </Box>
          )}
        </Box>
      </CollapsedFieldsLine>

      <Button data-testid='part-track-close' onClick={onClose} size='small' sx={{ mt: 2, textTransform: 'none' }}>{t_('Close')}</Button>
    </Box>
  )
}

export default PartTrackPanel
